import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { injectIntl, intlShape } from '../../../util/reactIntl';

import css from './SectionCollaborationLogos.module.css';

import Logo1 from './CompanyLogos/logo1.png';
import Logo2 from './CompanyLogos/logo2.png';
import Logo3 from './CompanyLogos/logo3.png';
import Logo4 from './CompanyLogos/logo4.png';
import Logo5 from './CompanyLogos/logo5.png';
import Logo6 from './CompanyLogos/logo6.png';

const mobileViewportQuery = '(max-width: 767px)';
const mobileLogosPerSlide = 2;
const mobileAutoplayDelay = 2600;

const deriveAltFromFilename = filename =>
  filename
    .replace(/\.[^/.]+$/, '')
    .replace(/[-_]+/g, ' ')
    .replace(/([a-zA-Z])(\d)/g, '$1 $2')
    .replace(/(\d)([a-zA-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const chunkItems = (items, chunkSize) =>
  items.reduce((chunks, item, index) => {
    if (index % chunkSize === 0) {
      chunks.push([item]);
    } else {
      chunks[chunks.length - 1].push(item);
    }

    return chunks;
  }, []);

const companyLogos = [
  { filename: 'logo1.png', src: Logo1 },
  { filename: 'logo2.png', src: Logo2 },
  { filename: 'logo3.png', src: Logo3 },
  { filename: 'logo4.png', src: Logo4 },
  { filename: 'logo5.png', src: Logo5 },
  { filename: 'logo6.png', src: Logo6 },
]
  .sort((a, b) => a.filename.localeCompare(b.filename, undefined, { numeric: true }))
  .map(logo => ({
    ...logo,
    alt: deriveAltFromFilename(logo.filename),
  }));

const isRtlLocale = locale => {
  const l = (locale || '').toLowerCase();
  return l === 'ar' || l.startsWith('ar-');
};

const SectionCollaborationLogosComponent = props => {
  const { intl, className, rootClassName } = props;

  const locale = intl?.locale || '';
  const rtl = isRtlLocale(locale);
  const mobileSlides = chunkItems(companyLogos, mobileLogosPerSlide);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  const classes = classNames(rootClassName || css.root, className);
  const ariaLabel = intl.formatMessage({ id: 'SectionCollaborationLogos.aria' });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQueryList = window.matchMedia(mobileViewportQuery);
    const handleViewportChange = event => {
      setIsMobileViewport(event.matches);
    };

    setIsMobileViewport(mediaQueryList.matches);

    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', handleViewportChange);

      return () => {
        mediaQueryList.removeEventListener('change', handleViewportChange);
      };
    }

    mediaQueryList.addListener(handleViewportChange);

    return () => {
      mediaQueryList.removeListener(handleViewportChange);
    };
  }, []);

  useEffect(() => {
    setActiveSlide(currentSlide => (currentSlide >= mobileSlides.length ? 0 : currentSlide));
  }, [mobileSlides.length]);

  useEffect(() => {
    if (!isMobileViewport || mobileSlides.length < 2) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setActiveSlide(currentSlide => (currentSlide + 1) % mobileSlides.length);
    }, mobileAutoplayDelay);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isMobileViewport, mobileSlides.length]);

  return (
    <section className={classes} dir={rtl ? 'rtl' : 'ltr'}>
      <div className={css.content}>
        <h3 className={css.title}>
          {intl.formatMessage({ id: 'SectionCollaborationLogos.title' })}
        </h3>

        <div
          className={css.desktopLogos}
          style={{ '--desktop-logo-count': companyLogos.length }}
          aria-label={ariaLabel}
        >
          {companyLogos.map(item => (
            <div key={item.filename} className={css.logoItem}>
              <img className={css.logoImg} src={item.src} alt={item.alt} loading="lazy" />
            </div>
          ))}
        </div>

        <div className={css.mobileCarousel} aria-label={ariaLabel} aria-roledescription="carousel">
          <div className={css.mobileViewport}>
            <div
              className={css.mobileTrack}
              style={{
                transform: `translateX(${activeSlide * -100}%)`,
              }}
            >
              {mobileSlides.map((slide, index) => (
                <div
                  key={`slide-${index + 1}`}
                  className={css.mobileSlide}
                  aria-hidden={index !== activeSlide}
                >
                  {slide.map(item => (
                    <div
                      key={item.filename}
                      className={classNames(css.logoItem, css.mobileLogoItem)}
                    >
                      <img className={css.logoImg} src={item.src} alt={item.alt} loading="lazy" />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {mobileSlides.length > 1 ? (
            <div className={css.mobileDots}>
              {mobileSlides.map((_, index) => (
                <button
                  key={`dot-${index + 1}`}
                  type="button"
                  className={classNames(css.dot, {
                    [css.activeDot]: index === activeSlide,
                  })}
                  onClick={() => setActiveSlide(index)}
                  aria-label={`${ariaLabel} ${index + 1}`}
                  aria-pressed={index === activeSlide}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

SectionCollaborationLogosComponent.defaultProps = {
  className: null,
  rootClassName: null,
};

SectionCollaborationLogosComponent.propTypes = {
  intl: intlShape.isRequired,
  className: PropTypes.string,
  rootClassName: PropTypes.string,
};

export default injectIntl(SectionCollaborationLogosComponent);
